'use client';

import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import Image from 'next/image';

import Icon from '@/components/ui/AppIcon';
import PartnerModal from './PartnerModal';

interface MetricData {
  label: string;
  value: string;
  unit: string;
  trend: string;
  icon: string;
}

interface SlideData {
  id: number;
  src: string;
  alt: string;
  label: string;
  sublabel: string;
  tag: string;
}

const HeroSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isPartnerModalOpen, setIsPartnerModalOpen] = useState(false);
  const [scanPos, setScanPos] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const metrics: MetricData[] = [
    { label: 'Active Projects', value: '247', unit: 'missions', trend: '+12%', icon: 'RocketLaunchIcon' },
    { label: 'Precision Rate', value: '91.2', unit: '%', trend: 'optimal', icon: 'CheckBadgeIcon' },
    { label: 'On-Time Delivery', value: '98.5', unit: '%', trend: '+3.2%', icon: 'ClockIcon' },
    { label: 'Quality Score', value: '9.8', unit: '/10', trend: 'excellent', icon: 'StarIcon' },
  ];

  const slides: SlideData[] = [
    {
      id: 1,
      src: '/assets/images/c47dd7eae1a4e8d93628b6d8a4b5c5da-1783346981205.jpg',
      alt: 'Rafale fighter jet in flight showcasing advanced aerospace engineering and precision manufacturing',
      label: 'RAFALE MULTIROLE FIGHTER',
      sublabel: 'Air Superiority · Combat Proven',
      tag: 'DEFENCE SYSTEMS',
    },
    {
      id: 2,
      src: '/assets/images/cec310ea05295e2a9b15375cecdfd9e3-1783346981249.jpg',
      alt: 'Advanced air defence missile system on launch platform with radar and targeting systems',
      label: 'AIR DEFENCE SYSTEM',
      sublabel: 'Integrated · Multi-Layer Protection',
      tag: 'MISSILE SYSTEMS',
    },
    {
      id: 3,
      src: '/assets/images/daa0c557faae3cb3efdeaf868d0b968f-1783346981204.jpg',
      alt: 'Fighter jet deploying countermeasures flares during tactical maneuver in low-light conditions',
      label: 'COUNTERMEASURES SUITE',
      sublabel: 'Electronic Warfare · Threat Defeat',
      tag: 'EW SYSTEMS',
    },
    {
      id: 4,
      src: '/assets/images/helicopter-h130-1783346981245.jpg',
      alt: 'H130 helicopter in flight over mountainous terrain showcasing rotary wing aerospace capability',
      label: 'AIRBUS HELICOPTER H-130 PROGRAMS',
      sublabel: 'Approved Supplier · H130 Program Parts',
      tag: 'ROTARY SYSTEMS',
    },
    {
      id: 5,
      src: '/assets/images/a3f01e3de9da9a0b8d8b51b90d50d93b-1783324608995.jpg',
      alt: 'Airbus approved supplier audited and listed in Airbus supplier portal for aerospace components',
      label: 'SATELLITE PAYLOAD STRUCTURAL SUPPORT',
      sublabel: '',
      tag: 'SPACE',
    },
    {
      id: 9,
      src: '/assets/images/1a24dadc0631a6dbf08233eea861ea7a-1783347726325.jpg',
      alt: 'SU-30 MKI twin-engine supermaneuverable multirole combat aircraft showcasing advanced aerospace engineering',
      label: 'SU-30 MKI FIGHTER',
      sublabel: 'Structural Components · Precision Manufactured',
      tag: 'DEFENCE SYSTEMS',
    },
    {
      id: 10,
      src: '/assets/images/652ebadb699610bb14dc425c8717ef4d-1783347726325.jpg',
      alt: 'HAL Tejas light combat aircraft with precision manufactured wings structural components, window frames and engine parts',
      label: 'HAL TEJAS PROGRAM',
      sublabel: 'Wings Structural · Window Frames · Engine Parts',
      tag: 'TEJAS LCA',
    },
    {
      id: 11,
      src: '/assets/images/Welcome_on_board_airbus_helicopters-2-1783347726340.jpg',
      alt: 'Airbus Helicopters approved supplier boarding pass certification document showing supplier qualification',
      label: 'AIRBUS APPROVED SOURCE',
      sublabel: 'Approved Supplier · Certified Program Partner',
      tag: 'AIRBUS COMMERCIAL AIRCARFT',
    },
    {
      id: 12,
      src: '/assets/images/4130457c9785d94dfb1e0b2e5809ffc5-1783347726342.jpg',
      alt: 'Airbus A380 double-deck wide-body commercial aircraft showcasing large-scale aerospace manufacturing',
      label: 'AIRBUS A380 PROGRAM',
      sublabel: 'Wide-Body Commercial · Precision Components',
      tag: 'AIRBUS A380',
    },
    {
      id: 13,
      src: '/assets/images/72ebe0eee76717fd16799c5f8c8b4768-1783347734785.jpg',
      alt: 'Advanced air defence radar system with phased array antenna for long-range threat detection and tracking',
      label: 'AIRBUS HELICOPTERS H-130',
      sublabel: 'Phased Array · Long-Range Detection Systems',
      tag: 'AIRBUS HELICOPTER PROGRAM',
    },
  ];

  const goToSlide = useCallback(
    (index: number) => {
      if (!isHydrated || isTransitioning) return;
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentSlide(index);
        setIsTransitioning(false);
      }, 350);
    },
    [isHydrated, isTransitioning]
  );

  const nextSlide = useCallback(() => {
    goToSlide((currentSlide + 1) % slides.length);
  }, [currentSlide, slides.length, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide((currentSlide - 1 + slides.length) % slides.length);
  }, [currentSlide, slides.length, goToSlide]);

  // Auto-cycle
  useEffect(() => {
    if (!isHydrated) return;
    const interval = setInterval(nextSlide, 4500);
    return () => clearInterval(interval);
  }, [isHydrated, nextSlide]);

  // Scan line animation
  useEffect(() => {
    if (!isHydrated) return;
    let frame: number;
    let pos = 0;
    const animate = () => {
      pos = (pos + 0.3) % 100;
      setScanPos(pos);
      frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [isHydrated]);

  const slide = slides[currentSlide];

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-background via-primary to-secondary overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 left-10 w-96 h-96 bg-accent rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-mission-orange rounded-full blur-3xl animate-pulse-slow delay-1000"></div>
      </div>

      <div className="relative mx-auto px-6 lg:px-8 pt-32 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Side - Hero Content */}
          <div className="space-y-8">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-muted/50 backdrop-blur-sm rounded-full border border-accent/30">
              <Icon name="ShieldCheckIcon" size={20} className="text-accent" />
              <span className="font-cta text-sm text-accent">AS9100 | ISO 14001 | ISO 27001 | ISO 45001 Certified</span>
            </div>

            <h1 className="font-headline text-5xl lg:text-7xl font-bold text-foreground leading-tight">
              Engineering the Future of{' '}
              <span className="text-transparent bg-clip-text from-accent to-mission-orange bg-[rgba(238,149,114,1)]">
                {'\n'}Global Manufacturing Intelligence
              </span>
            </h1>

            <p className="font-body text-sm text-accent font-semibold tracking-widest uppercase mb-2">
              One Partner&nbsp;.&nbsp;Unlimited Manufacturing Capability .&nbsp;On-time delivery
            </p>

            <p className="font-body text-xl leading-relaxed max-w-2xl text-orange-50">
              Whether your requirement demands precision machining, structural manufacturing, sheet-metal fabrication, tooling, sub-assemblies, or urgent production recovery, Wings Bharath becomes your&nbsp;single point of accountability—ensuring quality, traceability, and on-time delivery.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => setIsPartnerModalOpen(true)}
                className="group relative px-8 py-4 bg-mission-orange text-primary font-cta text-base font-medium rounded-lg overflow-hidden transition-all duration-300 hover:shadow-glow hover:-translate-y-1 text-center"
              >
                <span className="relative z-10 flex items-center justify-center space-x-2">
                  <span>BECOME A MANUFACTURING PARTNER</span>
                  <Icon name="ArrowRightIcon" size={20} className="group-hover:translate-x-1 transition-transform duration-300" />
                </span>
                <div className="absolute inset-0 bg-accent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
              </button>

              {/* Hi-Tech RFQ Button */}
              <Link
                href="/rfq"
                className="group relative px-8 py-4 font-cta text-base font-medium rounded-lg overflow-hidden transition-all duration-300 hover:-translate-y-1 text-center"
                style={{
                  background: 'linear-gradient(135deg, rgba(0,212,255,0.06) 0%, rgba(238,149,114,0.06) 100%)',
                  border: '1px solid transparent',
                }}
              >
                <span className="absolute inset-0 rounded-lg" style={{
                  padding: '1px',
                  background: 'linear-gradient(135deg, #00d4ff 0%, #ee9572 50%, #00d4ff 100%)',
                  backgroundSize: '200% 200%',
                  animation: 'heroRfqBorder 3s ease infinite',
                  WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                  WebkitMaskComposite: 'xor',
                  maskComposite: 'exclude',
                }} />
                <span className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-accent rounded-tl-lg" />
                <span className="absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2 border-accent rounded-tr-lg" />
                <span className="absolute bottom-0 left-0 w-3 h-3 border-b-2 border-l-2 border-mission-orange rounded-bl-lg" />
                <span className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-mission-orange rounded-br-lg" />
                <span className="absolute inset-0 overflow-hidden rounded-lg pointer-events-none">
                  <span className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/70 to-transparent"
                    style={{ animation: 'heroRfqScan 2.8s ease-in-out infinite' }} />
                </span>
                <span className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ background: 'radial-gradient(ellipse at center, rgba(0,212,255,0.12) 0%, transparent 70%)' }} />
                <span className="relative z-10 flex items-center justify-center space-x-3">
                  <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-60" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-accent" />
                  </span>
                  <Icon name="DocumentTextIcon" size={20} className="text-accent group-hover:scale-110 transition-transform duration-300" />
                  <span className="text-accent tracking-widest">REQUEST FOR QUOTATION</span>
                  <span className="font-mono text-accent/60 text-xs group-hover:translate-x-1 transition-transform duration-300">▶▶</span>
                </span>
              </Link>
            </div>

            {/* Real-Time Metrics Dashboard */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 pt-8">
              {metrics.map((metric) => (
                <div
                  key={metric.label}
                  className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-4 hover:border-accent transition-all duration-300 hover:shadow-glow"
                >
                  <div className="flex items-center justify-between mb-2">
                    <Icon name={metric.icon as any} size={24} className="text-accent" />
                    <span className="font-cta text-xs text-aerospace-green">{metric.trend}</span>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-baseline space-x-1">
                      <span className="font-headline text-2xl font-bold text-foreground">{metric.value}</span>
                      <span className="font-body text-sm text-technical-gray">{metric.unit}</span>
                    </div>
                    <p className="font-body text-xs text-technical-gray">{metric.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Side - Hi-Tech Image Slideshow Panel */}
          <div className="relative flex flex-col items-center justify-center">
            {/* Outer frame with corner accents */}
            <div className="relative w-full" style={{ maxWidth: 560 }}>
              {/* Top-left corner */}
              <span className="absolute -top-2 -left-2 w-6 h-6 border-t-2 border-l-2 border-accent z-20" />
              {/* Top-right corner */}
              <span className="absolute -top-2 -right-2 w-6 h-6 border-t-2 border-r-2 border-accent z-20" />
              {/* Bottom-left corner */}
              <span className="absolute -bottom-2 -left-2 w-6 h-6 border-b-2 border-l-2 border-mission-orange z-20" />
              {/* Bottom-right corner */}
              <span className="absolute -bottom-2 -right-2 w-6 h-6 border-b-2 border-r-2 border-mission-orange z-20" />

              {/* Main slide container */}
              <div
                className="relative overflow-hidden rounded-lg"
                style={{
                  aspectRatio: '4/3',
                  border: '1px solid rgba(0,212,255,0.25)',
                  background: '#000',
                  boxShadow: '0 0 40px rgba(0,212,255,0.12), inset 0 0 60px rgba(0,0,0,0.6)',
                }}
              >
                {/* Slide images */}
                {slides.map((s, i) => (
                  <div
                    key={s.id}
                    className="absolute inset-0 transition-opacity duration-700"
                    style={{ opacity: i === currentSlide && !isTransitioning ? 1 : 0 }}
                  >
                    <Image
                      src={s.src}
                      alt={s.alt}
                      fill
                      className="object-cover"
                      style={{
                        filter: 'brightness(0.82) contrast(1.1) saturate(0.9)',
                        transform: i === currentSlide && !isTransitioning ? 'scale(1.04)' : 'scale(1)',
                        transition: 'transform 4.5s cubic-bezier(0.25,0.46,0.45,0.94)',
                      }}
                      priority={i === 0}
                    />
                  </div>
                ))}

                {/* Scan line overlay */}
                <div
                  className="absolute left-0 right-0 pointer-events-none z-10"
                  style={{
                    top: `${scanPos}%`,
                    height: 2,
                    background: 'linear-gradient(90deg, transparent 0%, rgba(0,212,255,0.5) 30%, rgba(0,212,255,0.8) 50%, rgba(0,212,255,0.5) 70%, transparent 100%)',
                    boxShadow: '0 0 8px rgba(0,212,255,0.6)',
                    transition: 'top 0.05s linear',
                  }}
                />

                {/* CRT scanlines texture */}
                <div
                  className="absolute inset-0 pointer-events-none z-10"
                  style={{
                    backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.08) 2px, rgba(0,0,0,0.08) 4px)',
                  }}
                />

                {/* Vignette */}
                <div
                  className="absolute inset-0 pointer-events-none z-10"
                  style={{
                    background: 'radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.55) 100%)',
                  }}
                />

                {/* Top HUD bar */}
                <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-4 py-2"
                  style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.7) 0%, transparent 100%)' }}>
                  <div className="flex items-center space-x-2">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-70" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-accent" />
                    </span>
                    <span className="font-mono text-accent text-xs tracking-widest">LIVE FEED</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="font-mono text-accent/60 text-xs">
                      {String(currentSlide + 1).padStart(2, '0')}/{String(slides.length).padStart(2, '0')}
                    </span>
                    <span className="font-mono text-xs px-2 py-0.5 rounded"
                      style={{ background: 'rgba(0,212,255,0.15)', border: '1px solid rgba(0,212,255,0.3)', color: '#00d4ff' }}>
                      {slide.tag}
                    </span>
                  </div>
                </div>

                {/* Bottom info overlay */}
                <div className="absolute bottom-0 left-0 right-0 z-20 px-4 pb-4 pt-10"
                  style={{ background: 'linear-gradient(0deg, rgba(0,0,0,0.85) 0%, transparent 100%)' }}>
                  <div className="flex items-end justify-between">
                    <div>
                      <p className="font-mono text-xs text-accent/70 tracking-widest mb-1">▸ TARGET ACQUIRED</p>
                      <h3 className="font-headline text-white text-lg font-bold tracking-wide leading-tight">
                        {slide.label}
                      </h3>
                      <p className="font-mono text-xs text-accent/80 mt-1">{slide.sublabel}</p>
                    </div>
                    {/* Reticle icon */}
                    <div className="flex-shrink-0 ml-4 opacity-60">
                      <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                        <circle cx="18" cy="18" r="14" stroke="#00d4ff" strokeWidth="1" strokeDasharray="4 3" />
                        <circle cx="18" cy="18" r="6" stroke="#00d4ff" strokeWidth="1" />
                        <line x1="18" y1="4" x2="18" y2="10" stroke="#00d4ff" strokeWidth="1" />
                        <line x1="18" y1="26" x2="18" y2="32" stroke="#00d4ff" strokeWidth="1" />
                        <line x1="4" y1="18" x2="10" y2="18" stroke="#00d4ff" strokeWidth="1" />
                        <line x1="26" y1="18" x2="32" y2="18" stroke="#00d4ff" strokeWidth="1" />
                        <circle cx="18" cy="18" r="2" fill="#00d4ff" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Left/Right nav arrows */}
                <button
                  onClick={prevSlide}
                  className="absolute left-3 top-1/2 -translate-y-1/2 z-30 flex items-center justify-center w-8 h-8 rounded transition-all duration-200 hover:scale-110"
                  style={{
                    background: 'rgba(0,0,0,0.5)',
                    border: '1px solid rgba(0,212,255,0.35)',
                    backdropFilter: 'blur(4px)',
                  }}
                  aria-label="Previous slide"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M9 2L4 7L9 12" stroke="#00d4ff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <button
                  onClick={nextSlide}
                  className="absolute right-3 top-1/2 -translate-y-1/2 z-30 flex items-center justify-center w-8 h-8 rounded transition-all duration-200 hover:scale-110"
                  style={{
                    background: 'rgba(0,0,0,0.5)',
                    border: '1px solid rgba(0,212,255,0.35)',
                    backdropFilter: 'blur(4px)',
                  }}
                  aria-label="Next slide"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M5 2L10 7L5 12" stroke="#00d4ff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>

              {/* Navigation dots + progress bar row */}
              <div className="mt-4 flex items-center justify-between px-1">
                {/* Dots */}
                <div className="flex items-center space-x-2">
                  {slides.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => goToSlide(i)}
                      className="relative transition-all duration-300"
                      aria-label={`Go to slide ${i + 1}`}
                      style={{
                        width: i === currentSlide ? 28 : 8,
                        height: 8,
                        borderRadius: 4,
                        background: i === currentSlide
                          ? 'linear-gradient(90deg, #00d4ff, #ee9572)'
                          : 'rgba(0,212,255,0.25)',
                        border: i === currentSlide ? 'none' : '1px solid rgba(0,212,255,0.3)',
                        boxShadow: i === currentSlide ? '0 0 8px rgba(0,212,255,0.5)' : 'none',
                      }}
                    />
                  ))}
                </div>

                {/* Slide label strip */}
                <div className="flex items-center space-x-2">
                  <span className="font-mono text-xs text-accent/50 tracking-widest">LATEST PROJECTS STATUS</span>
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-aerospace-green opacity-70" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-aerospace-green" />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PartnerModal isOpen={isPartnerModalOpen} onClose={() => setIsPartnerModalOpen(false)} />

      <style jsx>{`
        @keyframes heroRfqBorder {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes heroRfqScan {
          0% { top: -2px; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
      `}</style>
    </section>
  );
};

export default HeroSection;